import TabList from '@/components/EntityManagement/TabList';
import PageContainer from '@/components/PageContainer.js';
import SelectClinic from '@/components/Select';
import PatientCard from '@/components/PatientCard';
import useFirequery from '@/hooks/useFirequery';

import { clinicCrud, patientCrud } from '@/services/firebase/firebase';
import { Clinic, ClinicAdmin, Doctor, Patient } from '@/types/database';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { Fab, Paper, TextField, Typography } from '@mui/material';
import { ChangeEvent, FC, useMemo, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useAuth } from '@/components/auth';
import { WhereArgs } from '@/services/firebase/FireCRUD';

const PatientManagementPage: FC = () => {
  const [clinicId, setClinicId] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = useMemo(() => {
    const role = user?.role;
    let query: WhereArgs | undefined;
    if (role === 'admin') {
      query = undefined;
    } else if (role === 'doctor') {
      query = ['id', 'in', (user as Doctor)?.associatedClinicIds];
    } else if (role === 'clinic-admin') {
      query = ['id', '==', (user as ClinicAdmin)?.managedClinicId];
    }

    return query;
  }, [user]);

  const { data: clinicsData, loading: loadingClinics } = useFirequery<Clinic[]>(clinicCrud.query(query), {
    disabled: !user,
  });

  const clinics = useMemo(
    () =>
      clinicsData?.map((clinic) => ({
        label: clinic.name,
        value: clinic.id,
      })) ?? [],
    [clinicsData]
  );

  const clinicValue = useMemo(() => clinics?.find((clinic) => clinic.value === clinicId), [clinicId, clinics]);

  const { data: patients, loading: loadingPatients } = useFirequery<Patient[]>(
    patientCrud.query(['clinicId', '==', clinicId]),
    { disabled: !clinicId }
  );

  const onChange = (e: ChangeEvent<HTMLInputElement>, change: { value: string }) => {
    setClinicId(change.value);
  };

  const onClickPatient = (patient: Patient) => {
    navigate(`${patient.id}`);
  };

  const [search, setSearch] = useState('');

  const filteredPatients = useMemo(() => {
    if (!patients) return [];
    if (!search.trim()) return patients;
    const lower = search.toLowerCase();
    return patients.filter(
      (p) =>
        p.name?.toLowerCase().includes(lower) ||
        p.email?.toLowerCase().includes(lower) ||
        p.id?.toLowerCase().includes(lower)
    );
  }, [patients, search]);

  const filteredTabs = useMemo(() => {
    return [
      {
        label: 'Active',
        items:
          filteredPatients
            ?.filter((patient) => patient.status !== 'archived')
            .map((patient) => ({
              ...patient,
              icon: 'mdi:user',
              labels: [patient.status],
            })) ?? [],
      },
      {
        label: 'Archived',
        items:
          filteredPatients
            ?.filter((patient) => patient.status === 'archived')
            .map((patient) => ({
              ...patient,
              icon: 'mdi:user',
              labels: [patient.status],
            })) ?? [],
      },
    ];
  }, [filteredPatients]);

  // Replace tabs in TabList with filteredTabs and pass search/setSearch to TextField

  return (
    <PageContainer title="managePatientsTitle" description="managePatientsDescription">
      <Paper sx={{ mt: 2, p: 2 }} square variant="outlined">
        <SelectClinic
          icon="lucide:building"
          fullWidth
          onChange={onChange}
          loading={loadingClinics}
          value={clinicValue}
          items={clinics}
          label="Clinic"
        />
      </Paper>
      <Paper square sx={{ mt: 2, p: 2 }} variant="outlined">
        <TextField
          size="small"
          fullWidth
          label="Search Patient"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Paper>
      <TabList
        component={PatientCard}
        onClickItem={onClickPatient}
        loading={loadingPatients}
        item
        sx={{ mt: 2 }}
        tabs={filteredTabs}
      />
      <Fab
        sx={{ position: 'absolute', bottom: 0, right: 10 }}
        variant="extended"
        disabled={!clinicId}
        size="medium"
        color="primary"
        component={NavLink}
        to={`${clinicId}/new/edit`}
      >
        <Icon style={{ marginRight: 10 }} icon="fa-solid:user-plus" />
        <Typography fontSize={14} textTransform="none">
          Invite patient
        </Typography>
      </Fab>
      <div style={{ height: '70px', position: 'relative', bottom: 0 }} />
    </PageContainer>
  );
};

export default PatientManagementPage;
